import React, { useEffect, useState } from 'react'
import { Modal, Button, Checkbox, Table } from 'semantic-ui-react'
import { CustomerUserService } from 'utility/services/customer-user'
import { LoaderBar } from 'components'
import styled from 'styled-components'
import { useAuth } from 'context'

const ScrollableContent = styled(Modal.Content)`
    max-height: 60vh;
    overflow-y: auto;
    
    .ui.table {
        border: none;
        margin-top: 0;
    }
`

export const InviteCustomerAssignModal = ({ open, onClose }) => {
    const [customers, setCustomers] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectAll, setSelectAll] = useState(false)
    const { user } = useAuth()

    useEffect(() => {
        if (open && user?.unique_id) {
            fetchCustomers(user.unique_id)
        }
    }, [open, user?.unique_id])

    const fetchCustomers = async userId => {
        try {
            setLoading(true)
            const response = await CustomerUserService.getByUserId(userId)
            setCustomers(response)
            // Check if all customers are selected
            const allSelected = response.every(customer => customer.IsSelected)
            setSelectAll(allSelected)
        } catch (error) {
            console.error('Error fetching customers:', error)
            setCustomers([])
        } finally {
            setLoading(false)
        }
    }

    const handleSelectAll = () => {
        const newSelectAll = !selectAll
        setSelectAll(newSelectAll)
        setCustomers(
            customers.map(customer => ({
                ...customer,
                IsSelected: newSelectAll,
            })),
        )
    }

    const handleCustomerSelect = customerId => {
        const updatedCustomers = customers.map(customer =>
            customer.CustomerId === customerId
                ? { ...customer, IsSelected: !customer.IsSelected }
                : customer,
        )
        setCustomers(updatedCustomers)
        setSelectAll(updatedCustomers.every(customer => customer.IsSelected))
    }

    const handleSave = async () => {
        try {
            setLoading(true)
            const selectedCustomerIds = customers
                .filter(customer => customer.IsSelected)
                .map(customer => customer.CustomerId)
                .join(';')

            onClose(true, selectedCustomerIds.split(';'))
        } catch (error) {
            console.error('Error saving customer assignments:', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal open={open} onClose={() => onClose(false)} size="small">
            <Modal.Header>Assign Customers</Modal.Header>
            <ScrollableContent>
                {loading && <LoaderBar content="Loading" />}
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    label="Select All"
                                />
                            </Table.HeaderCell>
                            <Table.HeaderCell>Customer Name</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {customers.length > 0 ? (
                            customers.map(customer => (
                                <Table.Row key={customer.CustomerId}>
                                    <Table.Cell collapsing>
                                        <Checkbox
                                            checked={customer.IsSelected}
                                            onChange={() =>
                                                handleCustomerSelect(
                                                    customer.CustomerId,
                                                )
                                            }
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        {customer.CustomerName ||
                                            'Unnamed Customer'}
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan="2" textAlign="center">
                                    No customers available
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </ScrollableContent>
            <Modal.Actions>
                <Button onClick={() => onClose(false)}>Cancel</Button>
                <Button primary onClick={handleSave} loading={loading}>
                    Save
                </Button>
            </Modal.Actions>
        </Modal>
    )
}
